import {
  ICart,
  ICartAttributesWithWeight,
  ICartItem,
  ICartItemsSapAvailabilities,
  ICartItemWithDetail,
  ITotalWeight,
} from '../models/cart.models';
import { MathUtils } from './math.utils';
import { IBaseModel, IMessage, IPriceDisputingPerItem } from '../models/common.models';
import { EMessageType, KGM, WEIGHT_KGM_LBS_CONVERSION_COEFFICIENT } from '../configurations/common';
import { IAbstractProductIncluded } from '../models/abstract-product.models';
import { AppUtils } from './app.utils';

export class CartUtils {
  static priceDisputingMessage: IMessage = {
    type: EMessageType.WARNING,
    title: 'cart.disputed-pricing.title',
    description: 'cart.disputed-pricing.description',
  };

  static isSetPricesForAllItems(cartItems: any): boolean {
    if (!cartItems?.length) {
      return false;
    }

    for (const item of cartItems) {
      if (item.attributes?.calculations?.originUnitPrice === 0 || item?.calculations?.originUnitPrice === 0) {
        return false;
      }
    }

    return true;
  }

  static getTotalPrice(isCartEmpty: boolean, currentCart: any): number {
    if (isCartEmpty) {
      return 0;
    }
    return currentCart?.totals?.subtotal || 0;
  }

  /**
   * Retrieve cart name for US store.
   *
   * @param {ICart} cart
   * @returns {string}
   */
  static getCartName(cart: ICart): string {
    return MathUtils.checkIfNumeric(cart.attributes.name)
      ? cart.id
      : cart.attributes.name;
  }

  static getClearEtag(eTag: string): string {
    return eTag.slice(eTag.indexOf('"')).replace(/"+/g, '');
  }

  static getIsMinimumOrderValue(
    minimumOrderValue: number | null,
    isCartEmpty: boolean,
    currentCart: any,
    cartItems: any,
    decimalDigit?: number,
  ): boolean {
    const storeMinimumOrderValue: number | null = minimumOrderValue;
    const totalOrderValue: number = this.getFormattedTotalPrice(isCartEmpty, currentCart, decimalDigit);

    if (this.isSetPricesForAllItems(cartItems)) {
      return storeMinimumOrderValue ? totalOrderValue >= storeMinimumOrderValue : true;
    }

    return true;
  }

  static mapAvailabilitiesToCartItems(
    cartItems: Array<ICartItem | ICartItemWithDetail>,
    cartIncludedResources: IBaseModel[],
  ): Array<ICartItemWithDetail> {
    const itemAvailabilities: ICartItemsSapAvailabilities = cartIncludedResources?.find(
      include => include.type === 'sap-item-availabilities',
    ) as ICartItemsSapAvailabilities;

    if (itemAvailabilities) {
      const itemAvailabilitiesByMatNo: Record<string, any> = {};
      for (const availabilityInfo of itemAvailabilities.attributes.itemAvailabilities) {
        itemAvailabilitiesByMatNo[availabilityInfo.materialNumber] = availabilityInfo;
      }

      return cartItems.map(
        cartItem => ({
          ...cartItem,
          attributes: {
            ...cartItem.attributes,
            availability: itemAvailabilitiesByMatNo[cartItem.attributes.materialNumber] ?? {},
          },
        }));
    } else {
      return cartItems;
    }
  }

  /**
   * Add exchangeCredit field to cart items with value from 'mcm_webshop_customer_price' attribute.
   *
   * @param {Array<ICartItem | ICartItemWithDetail>} cartItems
   * @param {IAbstractProductIncluded[]} cartIncludedResources
   * @returns {Array<ICartItemWithDetail>}
   */
  static mapExchangeCreditToCartItems(
    cartItems: Array<ICartItem | ICartItemWithDetail>,
    cartIncludedResources: IAbstractProductIncluded[],
  ): Array<ICartItemWithDetail> {
    if (cartIncludedResources) {
      return cartItems.map(
        cartItem => {
          const customerPrice: string | undefined = cartIncludedResources.find(
            concreteProduct => concreteProduct.id === cartItem.id,
          )?.attributes?.attributes?.['mcm_webshop_customer_price'];

          return {
            ...cartItem,
            attributes: {
              ...cartItem.attributes,
              exchangeCredit: customerPrice ? (Math.floor(Number(customerPrice) * 100) / 100) * AppUtils.getCurrentStore().defaultPriceDivisorValue : undefined,
            },
          };
        });
    } else {
      return cartItems;
    }
  }

  private static getFormattedTotalPrice(isCartEmpty: boolean, currentCart: any, decimalDigit: number = -0.1): number {
    if (isCartEmpty) {
      return 0;
    }

    let totalPrice: number;

    if (currentCart) {
      totalPrice = currentCart.attributes ? (currentCart.attributes.totals?.subtotal || 0) : (currentCart.totals?.subtotal || 0);
    } else {
      totalPrice = 0;
    }

    if (totalPrice || totalPrice !== 0) {
      totalPrice = totalPrice / (decimalDigit !== -0.1 ? Math.pow(10, decimalDigit) : 100);
    }

    return totalPrice;
  }

  static getCountOfItems(cartItems: any[]): number {
    return cartItems?.length || 0;
  }

  /**
   * Determine if price disputing is set for given item.
   *
   * @param {IPriceDisputingPerItem} priceDisputingPerItem
   * @param {string} itemId
   * @return {boolean}
   */
  static isPriceDisputingSetForItem(priceDisputingPerItem: IPriceDisputingPerItem[], itemId: string): boolean {
    return priceDisputingPerItem?.find(itemPriceDisputing => {
      return itemPriceDisputing.itemId === itemId;
    })?.priceDisputing?.isSet ?? false;
  }

  /**
   * Determine if equipment is required for product
   * This is used to show/hide "Select Equipment" modal when adding product to cart
   * - If supported categories list is empty, equipment is required for all products
   * - If My Installed Base Flow is disabled, equipment is not required
   * - If Non-Equipment Purchase is disabled, equipment is required
   * - If Non-Equipment Purchase is enabled, check if product category is supported for non-equipment purchase
   *
   * @param {boolean} isMyInstalledBaseFlowEnabled
   * @param {boolean} isNonEquipmentPurchaseEnabled
   * @param {string[]} supportedCategoriesForNonEquipmentPurchase
   * @param {string} productCategory - default to empty string
   * @returns {boolean}
   */
  static determineIfEquipmentIsRequired(
    isMyInstalledBaseFlowEnabled: boolean,
    isNonEquipmentPurchaseEnabled: boolean,
    supportedCategoriesForNonEquipmentPurchase: string[],
    productCategory: string = '',
  ): boolean {
    if (supportedCategoriesForNonEquipmentPurchase.length === 0) return true;
    if (!isMyInstalledBaseFlowEnabled) return false;
    if (!isNonEquipmentPurchaseEnabled) return true;
    return !supportedCategoriesForNonEquipmentPurchase.includes(productCategory);
  }

  static calculateCartTotalWeight(cartItemsWithDetails: ICartItemWithDetail[]): ITotalWeight {
    return cartItemsWithDetails.reduce((sum, item) => {
      const quantity: number = item?.attributes?.quantity ?? 0;
      const attrs: ICartAttributesWithWeight = item?.attributes?.attributes;

      if (!item?.attributes?.attributes) {
        return sum;
      }

      let kgmValue: number = 0;
      if (attrs?.sap_gross_weight_unit && attrs?.sap_gross_weight_unit === KGM) {
        kgmValue = attrs.sap_gross_weight ?? 0;
      } else if (attrs?.sap_p40_uom_weight && attrs?.sap_p40_uom_weight === KGM) {
        kgmValue = attrs.sap_p40_gross_weight ?? 0;
      }

      let lbsValue: number = 0;
      if (attrs?.sap_p40_uom_weight && attrs?.sap_p40_uom_weight !== KGM) {
        lbsValue = attrs.sap_p40_gross_weight ?? 0;
      } else if (attrs?.sap_gross_weight_unit && attrs?.sap_gross_weight_unit !== KGM) {
        lbsValue = attrs.sap_gross_weight ?? 0;
      }

      if (lbsValue && kgmValue) {
        sum.kgm += quantity * kgmValue;
        sum.lbs += quantity * lbsValue;
      } else if (lbsValue) {
        sum.lbs += quantity * lbsValue;
        sum.kgm += quantity * (lbsValue * WEIGHT_KGM_LBS_CONVERSION_COEFFICIENT);
      } else if (kgmValue) {
        sum.kgm += quantity * kgmValue;
        sum.lbs += quantity * (kgmValue / WEIGHT_KGM_LBS_CONVERSION_COEFFICIENT);
      }

      return sum;
    }, {kgm: 0, lbs: 0});
  }
}
